import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FolderOpen, CheckCircle2, AlertCircle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { documentsApi, handleApiError } from '@/services/api';
import { toast } from 'sonner';

interface DocumentUploaderProps {
  projectId: number;
  onUploadComplete: () => void;
}

interface UploadStats {
  total: number;
  successful: number;
  failed: number;
  errors?: string[];
}

export function DocumentUploader({ projectId, onUploadComplete }: DocumentUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState<UploadStats | null>(null);

  // Web: File picker
  const onFileDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      try {
        setUploading(true);
        setProgress(0);
        setUploadStats(null);

        // Upload files one by one
        let succeeded = 0;
        let failed = 0;

        for (let i = 0; i < acceptedFiles.length; i++) {
          const file = acceptedFiles[i];
          if (!file) continue;
          
          try {
            await documentsApi.upload(projectId, file);
            succeeded++;
            setProgress(Math.round(((i + 1) / acceptedFiles.length) * 100));
          } catch (error) {
            failed++;
            console.error(`Failed to upload file:`, error);
          }
        }

        const stats: UploadStats = {
          total: acceptedFiles.length,
          successful: succeeded,
          failed: failed,
        };

        setUploadStats(stats);

        if (succeeded > 0) {
          toast.success(`Successfully uploaded ${succeeded} file(s)`);
          onUploadComplete();
        }

        if (failed > 0) {
          toast.error(`Failed to upload ${failed} file(s)`);
        }
      } catch (error) {
        toast.error(handleApiError(error));
      } finally {
        setUploading(false);
      }
    },
    [projectId, onUploadComplete]
  );

  // Desktop: Folder picker with file scanning (Electron API)
  const selectFolder = async () => {
    // Check if we're in Electron environment
    const electronAPI = (window as any).electronAPI;
    
    if (electronAPI && electronAPI.selectFolderFiles) {
      try {
        setUploading(true);
        setProgress(0);
        
        // Clear existing documents from project first
        toast.info('Clearing existing documents...');
        await documentsApi.clearAll(projectId);
        
        const fileData: Array<{ name: string; data: number[]; path: string }> = await electronAPI.selectFolderFiles();
        
        if (!fileData || fileData.length === 0) {
          setUploading(false);
          toast.info('No supported files found in folder');
          return;
        }

        // Convert file data to File objects
        const files = fileData.map(({ name, data }) => {
          const uint8Array = new Uint8Array(data);
          // Determine MIME type from file extension
          const ext = name.toLowerCase().split('.').pop();
          let mimeType = 'application/octet-stream';
          if (ext === 'pdf') mimeType = 'application/pdf';
          else if (ext === 'docx') mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          else if (ext === 'doc') mimeType = 'application/msword';
          else if (ext === 'txt') mimeType = 'text/plain';
          else if (ext === 'eml') mimeType = 'message/rfc822';
          else if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';
          else if (ext === 'png') mimeType = 'image/png';
          
          const blob = new Blob([uint8Array], { type: mimeType });
          return new File([blob], name, { type: mimeType });
        });

        // Upload files using batch endpoint
        const response = await documentsApi.uploadMultiple(projectId, files);
        
        setUploadStats(response as UploadStats);
        toast.success(`Uploaded ${files.length} file(s) from folder successfully`);
        onUploadComplete();
      } catch (error) {
        console.error('Folder upload error:', error);
        toast.error(handleApiError(error));
      } finally {
        setUploading(false);
      }
    } else {
      toast.info('Folder upload is only available in the desktop app');
    }
  };

  // Web folder upload using directory picker
  const handleWebFolderUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Convert FileList to Array and filter supported types
    const fileArray = Array.from(files).filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      return ['pdf', 'docx', 'doc', 'eml', 'jpg', 'jpeg', 'png'].includes(ext || '');
    });

    if (fileArray.length === 0) {
      toast.info('No supported files found in folder');
      return;
    }

    toast.info(`Found ${fileArray.length} files in folder and subfolders. Uploading...`);
    await onFileDrop(fileArray);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onFileDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'message/rfc822': ['.eml'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    disabled: uploading,
    multiple: true,
  });

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <Card>
        <CardContent className="p-6">
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-12
              text-center cursor-pointer transition-colors
              ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
              ${uploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50'}
            `}
            role="button"
            tabIndex={0}
          >
            <input {...getInputProps()} aria-label="Upload documents" />
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">
              {isDragActive ? 'Drop files here' : 'Upload Documents'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Drag and drop files or click to browse
            </p>
            <div className="flex gap-2 justify-center flex-wrap">
              <Badge variant="outline">PDF</Badge>
              <Badge variant="outline">DOCX</Badge>
              <Badge variant="outline">Email</Badge>
              <Badge variant="outline">Images</Badge>
            </div>
          </div>

          {/* Folder Upload */}
          <div className="mt-4 flex justify-center gap-2">
            {/* Web folder upload (works in modern browsers) */}
            <label htmlFor="folder-upload">
              <Button variant="outline" disabled={uploading} asChild>
                <span className="cursor-pointer">
                  <FolderOpen className="h-4 w-4 mr-2" />
                  Select Folder (All Subfolders)
                </span>
              </Button>
            </label>
            <input
              id="folder-upload"
              type="file"
              /* @ts-ignore - webkitdirectory is not in TS types but works in all modern browsers */
              webkitdirectory=""
              directory=""
              multiple
              onChange={handleWebFolderUpload}
              className="hidden"
              disabled={uploading}
            />
            
            {/* Desktop-specific folder upload (Electron) */}
            {(window as any).electronAPI && (
              <Button variant="outline" onClick={selectFolder} disabled={uploading}>
                <FolderOpen className="h-4 w-4 mr-2" />
                Desktop Folder Picker
              </Button>
            )}
          </div>

          {/* Progress */}
          {uploading && (
            <div className="mt-6 space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-sm text-center text-muted-foreground">
                Uploading... {progress}%
              </p>
            </div>
          )}

          {/* Stats */}
          {uploadStats && !uploading && (
            <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Upload Summary
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-2xl font-bold">{uploadStats.total}</div>
                  <div className="text-xs text-muted-foreground">Total Files</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {uploadStats.successful}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <CheckCircle2 className="inline h-3 w-3 mr-1" />
                    Successful
                  </div>
                </div>
                {uploadStats.failed > 0 && (
                  <div>
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {uploadStats.failed}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <AlertCircle className="inline h-3 w-3 mr-1" />
                      Failed
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            Smart Annotation Import
          </h4>
          <p className="text-sm text-muted-foreground">
            If your documents already have annotations or summaries (like .json or .txt files), we'll
            automatically import them instead of reprocessing. This saves time and preserves your
            existing work!
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
